import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import { XMarkIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../../lib/supabase';
import { useProject } from '../../../../context/ProjectContext';
import EditableTitle from '../../common/EditableTitle';

export default function UpdateDataSettingsModal({ 
  isOpen, 
  onClose, 
  data, 
  onSave,
  nodeName 
}) {
  const { currentProject } = useProject();
  const hasUpstreamConnection = data.edges?.some(edge => 
    edge.target === data.id
  );

  const [settings, setSettings] = useState({
    label: data.label || nodeName,
    selectedUpstreamNodes: data.selectedUpstreamNodes || [],
    ...data
  });

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Fetch available tables when modal opens
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const { data: dataTables, error } = await supabase
          .from('data_tables')
          .select('*')
          .eq('project_id', currentProject.id);

        if (error) throw error;
        setTables(dataTables || []);
      } catch (error) {
        console.error('Error fetching tables:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentProject?.id) {
      fetchTables();
    }
  }, [currentProject?.id]);

  const upstreamNodes = React.useMemo(() => {
    if (!data.edges || !data.nodes) return [];
    
    const visited = new Set();
    const nodes = [];
  
    const findUpstreamNodes = (currentNodeId) => {
      if (visited.has(currentNodeId)) return;
      visited.add(currentNodeId);
  
      const incomingEdges = data.edges.filter(edge => edge.target === currentNodeId);
      
      for (const edge of incomingEdges) {
        const sourceNode = data.nodes.find(node => node.id === edge.source);
        
        if (sourceNode && sourceNode.type !== 'trigger') {
          nodes.push({
            nodeId: sourceNode.id,
            type: sourceNode.type,
            label: sourceNode.data.label || `Unnamed ${sourceNode.type} Node`
          });
        }
        
        findUpstreamNodes(edge.source);
      }
    };
  
    findUpstreamNodes(data.id);
    return nodes;
  }, [data.edges, data.nodes, data.id]);

  const handleNodeSelection = (nodeId) => {
    setSettings(prev => {
      const selectedNodes = prev.selectedUpstreamNodes || [];
      if (selectedNodes.includes(nodeId)) {
        return {
          ...prev,
          selectedUpstreamNodes: selectedNodes.filter(id => id !== nodeId)
        };
      } else {
        return {
          ...prev,
          selectedUpstreamNodes: [...selectedNodes, nodeId]
        };
      }
    });
  };

  const handleSubmit = () => {
    onSave(settings);
    onClose();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
      return;
    }
    data.onDelete(data.id);
    onClose();
  };

  // If no upstream connection, show the connection required message
  if (!hasUpstreamConnection) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Connect Node First</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <p className="text-gray-400">
            Please connect this node to an agent node before configuring settings.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleDelete}
              className={`flex items-center gap-1 text-sm transition-colors ${
                deleteConfirm 
                  ? 'bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded' 
                  : 'text-gray-400 hover:text-red-500'
              }`}
            >
              <XMarkIcon className="w-3.5 h-3.5" />
              <span>{deleteConfirm ? 'Click to confirm delete' : 'Delete node'}</span>
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Regular modal content when there is an upstream connection
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={`
        flex h-[800px] max-h-[85vh]
        ${upstreamNodes.length === 0 
          ? 'w-[500px]'
          : 'w-[1200px]'
        }
        max-w-[90vw]
      `}>
        {/* Left side - Upstream Nodes */}
        <div className="w-[40%] bg-dark-300 p-6 border-r border-dark-400">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium">Include Results From</h3>
          </div>
          <div className="bg-dark-200 rounded-lg border border-dark-400 h-[calc(100%-60px)] overflow-y-auto px-2">
            <div className="divide-y divide-dark-400">
              {upstreamNodes.map((node) => (
                <button
                  key={node.nodeId}
                  onClick={() => handleNodeSelection(node.nodeId)}
                  className={`w-full flex items-center p-4 my-2 rounded-lg hover:bg-dark-400 transition-colors ${
                    settings.selectedUpstreamNodes.includes(node.nodeId)
                      ? 'bg-accent-primary/10 border border-accent-primary'
                      : 'bg-dark-300 border border-dark-400'
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-200">{node.label}</div>
                    <div className="text-sm text-gray-400">{node.type}</div>
                  </div>
                  <div className={`w-3 h-3 rounded-full transition-colors ${
                    settings.selectedUpstreamNodes.includes(node.nodeId)
                      ? 'bg-accent-primary'
                      : 'bg-gray-600'
                  }`} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Settings */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="mb-6 flex items-center justify-between">
            <EditableTitle 
              title={settings.label || nodeName || 'Update Data Table'} 
              onSave={(newTitle) => {
                setSettings(prev => ({
                  ...prev,
                  label: newTitle
                }));
              }}
              className="text-xl font-semibold text-gray-900 dark:text-white"
            />
            <button 
              onClick={handleSubmit}
              className="text-gray-400 hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form className="space-y-6">
            {/* Table Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Select Table to Update</label>
              {loading ? (
                <div className="text-sm text-gray-400">Loading tables...</div>
              ) : (
                <select
                  value={settings.selectedTable?.id || ''}
                  onChange={(e) => {
                    const table = tables.find(t => t.id === e.target.value);
                    setSettings(prev => ({ ...prev, selectedTable: table }));
                  }}
                  className="w-full px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary"
                  required
                >
                  <option value="">Select a table</option>
                  {tables.map(table => (
                    <option key={table.id} value={table.id}>
                      {table.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium mb-2">Update Instructions</label>
              <textarea
                value={settings.instructions}
                onChange={(e) => setSettings(prev => ({ ...prev, instructions: e.target.value }))}
                className="w-full h-32 px-3 py-2 bg-dark-200 border border-divider rounded focus:outline-none focus:border-accent-primary resize-none"
                placeholder="Provide instructions for how to update the table using the selected node's output..."
                required
              />
            </div>
          </form>
        </div>
      </div>

      {/* Footer - Only Delete Button */}
      <div className="border-t border-dark-400 p-4">
        <button
          type="button"
          onClick={handleDelete}
          className={`flex items-center gap-1 text-sm transition-colors ${
            deleteConfirm 
              ? 'bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded' 
              : 'text-gray-400 hover:text-red-500'
          }`}
        >
          <XMarkIcon className="w-3.5 h-3.5" />
          <span>{deleteConfirm ? 'Click to confirm delete' : 'Delete node'}</span>
        </button>
      </div>
    </Modal>
  );
} 